import { CommonModule } from '@angular/common';
import { Component, NO_ERRORS_SCHEMA, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogCloseResult, DialogService, DialogsModule, WindowCloseResult, WindowModule, WindowRef, WindowService } from '@progress/kendo-angular-dialog';
import { GridModule, PagerPosition, PagerType } from '@progress/kendo-angular-grid';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { PagerModule } from '@progress/kendo-angular-pager';
import { MulticheckFilterComponent } from '../../filters/multicheck-filter/multicheck-filter.component';
import { ErrorDialogModalComponent } from '../../common-components/error-dialog-modal/error-dialog-modal.component';
import { DeleteConfirmComponent } from '../../common-components/delete-confirm/delete-confirm.component';
import { Permission, PermissionNew, PermissionType } from '../../models/models.lib';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { CompositeFilterDescriptor, distinct, filterBy, orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { CasiCloseOptions, FormModes, AppEnities, defaultGridPageSize } from '../../common/constants.lib';
import { PermissionService } from '../permission.service';
import { ToastrService } from 'ngx-toastr';
import {
  faPlusCircle, faAddressCard,
  faEdit, faTrash
} from '@fortawesome/free-solid-svg-icons';
import { setTitle } from '../../common/utils.lib';
import { EditPermissionModalComponent } from './edit-permission-modal/edit-permission-modal.component';

import { all } from '@awesome.me/kit-787edf9aa0/icons';
import { AddEditPermissionModalComponent } from './add-edit-permission-modal/add-edit-permission-modal.component';
import { LoggerService } from '../../shared/logger.service';
import { MeService } from '../../auth/me.service';

const { PERMISSION } = AppEnities;

@Component({
  selector: 'casi-permissions',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    InputsModule,
    ButtonsModule,
    DialogsModule,
    WindowModule,
    PagerModule,
    GridModule,
    MulticheckFilterComponent,
    ErrorDialogModalComponent,
    DeleteConfirmComponent,
    EditPermissionModalComponent
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './permissions.component.html',
  styleUrl: './permissions.component.scss'
})
export class PermissionsComponent implements OnInit, OnDestroy {

  @ViewChild("appendTo", { read: ViewContainerRef }) appendTo: ViewContainerRef | undefined;

  public view: Observable<Permission[]> = new Observable<Permission[]>();
  public rawData: Permission[] = [];
  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };
  public gridData: Permission[] = filterBy(this.rawData, this.filter);

  public permissionSet: Permission[] = []

  public currentItem: PermissionType = PermissionNew.create();

  public formMode: FormModes = FormModes.NOTSET;

  public editWinOpen: boolean = false;
  public errorWinOpen: boolean = false;
  public confirmDeleteRole: boolean = false;
  public accessRightsWinOpen: boolean = false;
  public errorMsgOpen: boolean = false;
  public canUpdate: boolean = false;
  public canCreate: boolean = false;
  public canDestroy: boolean = false;

  public pagerTypes = ["numeric", "input"];
  public type: PagerType = "numeric";
  public info = true;
  public pageSizes = true;
  public previousNext = true;
  public position: PagerPosition = "bottom";

  public pageSize = defaultGridPageSize;

  private _showTitle = new BehaviorSubject<string>('');
  get showTitle() {
    return this._showTitle.getValue();
  }
  set showTitle(val: string) {
    this._showTitle.next(val);
  }

  private _windowRef: WindowRef | undefined;

  private _subs: Subscription[] = [];

  constructor(
    private logger: LoggerService,
    public dataService: PermissionService,
    public faIcons: FaIconLibrary,
    private toastr: ToastrService,
    private dialogService: DialogService,
    private windowService: WindowService,
    private me: MeService
  ) {
    faIcons.addIcons(
      faPlusCircle, faAddressCard, faEdit,
      faTrash
    );
    faIcons.addIcons(...all);
  }

  public sort: SortDescriptor[] = [
    {
      field: "name",
      dir: "asc",
    },
  ];

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');
    const { can_Create, can_Update, can_Destroy } = this.me.allICanDo(PERMISSION)
    this.canUpdate = can_Update;
    this.canCreate = can_Create;
    this.canDestroy = can_Destroy;
    // this.view = this.dataService;
    this.dataService.query();
    this._subs.push(this.dataService.subscribe(v => {
      this.logger.debug('ngOnInit:v: ', v);
      this.rawData = v;
      this.loadData();
    }));
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  public onAddPermission(titleBar: TemplateRef<unknown>): void {
    this.logger.debug("onAddPermission...");
    this.currentItem = PermissionNew.create();
    this.formMode = FormModes.NEW;
    this.logger.debug("onAddPermission:currentItem ", this.currentItem);
    this.showTitle = setTitle(this.formMode, 'PERMISSION');
    // this.openAddEditWindow(this.formMode, this.currentItem, titleBar);
    this.openAddEditDialog(this.formMode, this.currentItem);
    // this.showTitle = setTitle(this.formMode, 'PERMISSION');
    // this.editWinOpen = true;
  }

  onEditClick(dataItem: any, titleBar: TemplateRef<unknown>): void {
    this.logger.debug("onEditClick: ", dataItem);
    this.currentItem = Permission.create(dataItem);
    this.formMode = FormModes.EDIT;
    this.showTitle = setTitle(this.formMode, 'PERMISSION');
    this.logger.debug("onEditClick:currentItem ", this.currentItem);
    // this.openAddEditWindow(this.formMode, this.currentItem, titleBar);
    this.openAddEditDialog(this.formMode, this.currentItem);
    // this.showTitle = setTitle(this.formMode, 'PERMISSION');
    // this.editWinOpen = true;
  }

  onEditPermisions(dataItem: any): void {
    this.logger.debug("onEditPermisions: ", dataItem, this.permissionSet);
    this.currentItem = dataItem;
    this.accessRightsWinOpen = true;
  }

  onConfirmDelete(dataItem: any): void {
    this.logger.debug("onConfirmDelete: ", dataItem);
    this.currentItem = dataItem;
    this.openConfirmDailog(this.currentItem);
    // this.confirmDeleteRole = true;
  }

  onConfirmedClose(status: string): void {
    this.logger.debug("onConfirmedClose: ", status, this.currentItem);
    this.confirmDeleteRole = false;
    if (status === 'delete') {
      this.handleRemove(this.currentItem);
    }
  }

  //#region Grid function

  public sortChange(sort: SortDescriptor[]): void {
    this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    this.loadData();
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    // this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.loadData();
    // this.gridData = filterBy(this.rawData, filter);
  }

  public distinctPrimitive(fieldName: string): any[] {
    // this.logger.debug('distinctPrimitive: fieldName: ', fieldName);
    return distinct(this.rawData, fieldName).map((item: any) => item[fieldName]);
    // return distinct(this.gridData, fieldName).map((item: any) => item[fieldName]);
  }

  //#endregion

  //#region Private function

  private openAddEditWindow(mode: FormModes, dataItem: PermissionType, titleBar: TemplateRef<unknown>): void {
    this.logger.debug("openAddEditWindow...");
    this.closeWindowRef()
    this._windowRef = this.windowService.open({
      titleBarContent: titleBar,
      content: AddEditPermissionModalComponent,
      minWidth: 320,
      width: 450,
      // preventClose: (ev, window) => {
      //   this.logger.debug(`preventClose: `, ev, window?.content.instance.permissionForm);
      //   const myForm = window?.content.instance.premissionForm;
      //   this.logger.debug(`preventClose: `, myForm.valid);
      //   if (!myForm.valid) {
      //     myForm.get("entity").markAsTouched();
      //   }
      //   return !myForm.valid;
      // }
      // cssClass: "custom-css-class"
    });

    this.setAddEditInstanceInputs(mode, dataItem);
    // const contentRef = dialogRef.content.instance as EditPermissionModalComponent;
    // // contentRef.title = setTitle(mode, 'PERMISSION');
    // contentRef.permission = dataItem;
    // contentRef.mode = mode;

    this._windowRef.content.instance.submitForm.subscribe((formData: FormGroup) => {
      this.logger.debug('openAddEditWindow:formData: ', formData);
      this.handleClose(formData.value, mode);
    });

    this._windowRef.result.subscribe((r: any) => {
      this.logger.debug('openAddEditWindow:result:', r);
      if (r instanceof WindowCloseResult) {
        this.closeWindowRef();
        // if (dialogRef) {
        //   dialogRef.close();
        // }
        // return;
      }
      // if (r.text === CasiCloseOptions.Saved) {
      //   this.logger.debug("openAddEditWindow:saved... ", contentRef.permissionForm.value);
      //   this.handleClose(contentRef.permissionForm.value, mode);
      // }
    });

    this._windowRef.content.instance.closeForm.subscribe(() => {
      this.logger.debug('openAddEditWindow:closeForm');
      this.closeWindowRef();
    });

    this.editWinOpen = true;
  }

  private openAddEditDialog(mode: FormModes, dataItem: PermissionType): void {
    this.logger.debug("openAddEditDialog...");
    const dialogRef = this.dialogService.open({
      content: EditPermissionModalComponent,
      minWidth: 320,
      width: 450,
      // cssClass: "custom-css-class"
    });

    const contentRef = dialogRef.content.instance as EditPermissionModalComponent;
    contentRef.title = setTitle(mode, 'PERMISSION');
    contentRef.permission = dataItem;
    contentRef.mode = mode;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openAddEditDialog:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Saved) {
        this.logger.debug("openAddEditDialog:saved... ", contentRef.permissionForm.value);
        this.handleClose(contentRef.permissionForm.value, mode);
      }
    });
  }

  private loadData(): void {
    this.logger.debug('loadData...');
    this.gridData = orderBy(filterBy(this.rawData, this.filter), this.sort);
    this.pageSize = this.gridData.length;
  }

  private handleClose(data: any, mode: FormModes): void {
    this.logger.debug('handleClose...', data, mode);
    let dup: boolean = false;
    if (data) {
      let inUse = this.rawData.find(r => r.entity === data.entity && r.action === data.action);
      this.logger.debug('handleClose...inUse', inUse);
      if (inUse) {
        if (mode === FormModes.NEW) {
          dup = true;
        } else {
          dup = inUse.id !== data.id;
        }
      }
      if (dup) {
        this.logger.warn(`handleClose: permission: ${data.entity}/${data.action} already in use `);
        this.openErrorDailog("Duplicate Permission Name", "That Permission already exists!", "Close and Retry")
      } else
        this.savePermission(data, mode);
    }
  }

  private handleAccessRightClose(): void {
    this.logger.debug('handleAccessRightClose...');
    this.dataService.query();
    this.accessRightsWinOpen = false;
  }

  private handleRemove(args: any): void {
    if (args && !args.id) {
      this.logger.error('handlRemove: No id given');
    }

    this.dataService.destroy(args.id)
      .then(resp => {
        this.logger.debug('handleRemove:resp: ', resp);
        this.notify("Permission Deleted");
        this.dataService.query();
      })
      .catch(err => {
        this.logger.error('handleRemove:err: ', err);
        this.notify("Error Deleting Permission", true);
        this.openErrorDailog("Update Issue", `The current permission: ${this.currentItem.entity} has permissions. Please remove permissions first before deleting.`);
      });
  }

  private savePermission(permission: any, mode: FormModes): void {
    this.logger.debug('savePermission...', permission, mode);
    if (mode === FormModes.EDIT) {
      this.dataService.update(permission)
        .then(resp => {
          this.logger.debug('savePermission:update:resp: ', resp);
          this.notify("Permission Updated");
          this.closeWindowRef();
          this.dataService.query();
        })
        .catch(err => {
          this.logger.error('savePermission:update:err: ', err);
          this.notify("Error Updating Permission", true);
        });
    } else {
      this.dataService.add(permission)
        .then(resp => {
          this.logger.debug('savePermission:add:resp: ', resp);
          this.notify("Permission Added");
          this.closeWindowRef();
          this.dataService.query();
        })
        .catch(err => {
          this.logger.error('savePermission:add:err: ', err);
          this.notify("Error Creating Permission", true);
        });
    }
  }

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error)
      this.toastr.error(msg);
    else
      this.toastr.success(msg);
  }

  private openConfirmDailog(dataItem: any): void {
    this.logger.debug("openConfirmDailog: ", dataItem);
    this.currentItem = dataItem;
    const dialogRef = this.dialogService.open({
      content: DeleteConfirmComponent,
      minWidth: 250,
      width: 450,
      // cssClass: "custom-css-class"
    });

    const contentRef = dialogRef.content.instance as DeleteConfirmComponent;
    contentRef.title = "CONFIRM DELETE";
    contentRef.content = `Delete Permission - <b>${this.currentItem.entity}/${this.currentItem.action}</b>?`;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openDialogForm:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Ok) {
        this.logger.debug("openConfirmDailog:ok to proceed... ", this.currentItem);
        this.handleRemove(this.currentItem);
      }
    });
  }

  private openErrorDailog(title: string = 'Not set', content: string = 'No content', buttonText: string = 'Close'): void {
    const dialogRef = this.dialogService.open({
      content: ErrorDialogModalComponent,
      minWidth: 320,
      width: 750,
      cssClass: "custom-css-class"
    });

    const contentRef = dialogRef.content.instance as ErrorDialogModalComponent;
    contentRef.title = title;
    contentRef.content = content;
    contentRef.buttonText = buttonText;
  }

  private setAddEditInstanceInputs(mode: FormModes, dataItem: PermissionType): void {
    if (this._windowRef) {
      const inputs = this._windowRef.content.instance as AddEditPermissionModalComponent;
      inputs.permission = dataItem;
      inputs.mode = mode;
      this.logger.debug('setAddEditInstanceInputsinputs: ', inputs);
    }
  }

  private closeWindowRef(): void {
    if (this._windowRef) {
      this._windowRef.close();
      this._windowRef = undefined;
    }
    this.editWinOpen = false;
  }

  //#endregion

}
